import {
    RiDeleteBinLine, RiEdit2Line,
    RiFileCopyLine,
    RiLayout6Line,
    RiLinkM,
} from "@remixicon/react";
import React, {
    ReactNode,
    useState,
} from "react";
import {
    ChannelInvite,
    GetChannelInvitesDocument,
    useDeleteChannelInviteMutation,
    useGetChannelInvitesQuery,
} from "../../../../__generated__/api";
import useRouting from "../../../../contexts/Routing";

import useDeleteConfirm from "../../../../components/confirm/Delete";
import Button from "../../../../components/theme/button/Button";
import Link from "../../../../components/theme/button/Link";
import SettingsContainer from "../../../../components/theme/settings";
import createTableContainer from "../../../../components/theme/table";
import Text from "../../../../components/theme/text/Text";
import useArticleContext from "../../../../contexts/HelpContext";
import { SettingsProps } from "../base";
import Styles from "./ChannelInvites.module.scss";
import TimedMessage from "../../../../components/timed-message/TimedMessage";

const TableContainer = createTableContainer<ChannelInvite>();

export default function ChannelInvitesTable(props: SettingsProps): ReactNode {
    useArticleContext("urn:fq:article:22c4892ab6ad48d5ac58d5a9f72559c4");

    const { data, error, loading: isLoading } = useGetChannelInvitesQuery();
    const [deleteInvite] = useDeleteChannelInviteMutation();

    const { element: deleteElement, setDeleting } = useDeleteConfirm<ChannelInvite>(
        async item => {
            await deleteInvite({
                refetchQueries: [
                    { query: GetChannelInvitesDocument },
                ],
                variables: {
                    id: item.id,
                },
            });
        },
        item => item.name,
        "Remove Channel Invite",
        "Once you delete the channel invite, the invite will no longer be availabe. However, "
        + "this will not impact any channels already created.",
        "Remove",
    );

    const { Paths, createHistoryPush } = useRouting();

    const [isCopied, setIsCopied] = useState(false);
    const doCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setIsCopied(true);
    };
    return (
        <>
            <TimedMessage shouldShow={isCopied} variant="success" seconds={3} onClose={() => setIsCopied(false)}>
                Link copied to clipboard
            </TimedMessage>
            <SettingsContainer navigation="none">
                <SettingsContainer.Section title="Channel Invites" shouldDisplayTitle={false}>
                    <SettingsContainer.Setting
                        title="Channel Invites"
                        description="."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isFullWidth
                    >
                        <TableContainer data={data?.channelInvites as ChannelInvite[]} isLoading={isLoading} error={error}>
                            <TableContainer.Empty>
                                <TableContainer.Empty.Default
                                    text="Simplify the process of connecting with your customers by
                                        letting them self-enroll for support. Just share a specially created URL with them.
                                        As they fill in their name, email, and company name, a new channel is
                                        automatically set up using tools like Slack's Connect feature, along with
                                        a personalized warm welcome message. This means less manual work for you and
                                        quicker, smoother interactions for your customers."
                                    title="Channel Templates"
                                    icon={<RiLayout6Line />}
                                    onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                        setting: "channel-invites",
                                        id: "new",
                                    })}
                                    buttonText="Add Channel Invite"
                                />
                            </TableContainer.Empty>
                            <TableContainer.TableControls>

                                <TableContainer.TableControls.Search
                                    search={(query, item) => item.name?.toLocaleLowerCase().includes(query)
                                                || item.description?.toLocaleLowerCase().includes(query)
                                                || item.channelNameTemplate?.toLocaleLowerCase().includes(query)}
                                />
                                <TableContainer.TableControls.Seperator />

                                <TableContainer.TableControls.SortBy />
                                <TableContainer.TableControls.Create
                                    text="Add Channel Invite"
                                    onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                        setting: "channel-invites",
                                        id: "new",
                                    })}
                                />
                            </TableContainer.TableControls>
                            <TableContainer.Table>

                                <TableContainer.TableValue
                                    columnKey="name"
                                    title="Name"
                                    sortBy={item => item.description?.toLowerCase()}
                                >
                                    {item => item.description}
                                </TableContainer.TableValue>

                                <TableContainer.TableValue
                                    columnKey="brandName"
                                    title="Brand Name"
                                    sortBy={item => item.name?.toLowerCase()}
                                >
                                    {item => item.name}
                                </TableContainer.TableValue>

                                <TableContainer.TableValue
                                    columnKey="channelNameTemplate"
                                    title="Channel Name Template"
                                    sortBy={item => item.channelNameTemplate?.toLowerCase()}
                                >
                                    {item => item.channelNameTemplate}
                                </TableContainer.TableValue>

                                <TableContainer.TableValue
                                    columnKey="link"
                                    title="Link"
                                >
                                    {item => {
                                        const link = `https://${window.location.hostname}/channel-invite/${item.id}`;

                                        return (
                                            <Text
                                                colorVariant="text-link"
                                                textVariant="body-2-regular"
                                                className={Styles.link}
                                            >
                                                <Link
                                                    href={link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    variant="none"
                                                    prefixIcon={<RiLinkM />}
                                                />
                                                <div>
                                                    {link}
                                                </div>
                                                <Button
                                                    variant="outline-blue"
                                                    prefixIcon={<RiFileCopyLine />}
                                                    size="small"
                                                    onClick={() => doCopy(link)}
                                                />
                                            </Text>
                                        );
                                    }}
                                </TableContainer.TableValue>

                                <TableContainer.Action
                                    icon={<RiEdit2Line />}
                                    altText="Edit Channel Group"
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    getLinkParams={item => ({
                                        setting: "channel-invites",
                                        id: item.id,
                                    })}
                                />
                                <TableContainer.Action
                                    icon={<RiDeleteBinLine />}
                                    altText="Delete Channel Tag"
                                    onClick={setDeleting}
                                />

                            </TableContainer.Table>
                        </TableContainer>
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>
            </SettingsContainer>
            {deleteElement}
        </>
    );
}
